import clsx from 'clsx'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSidebarLinkClicked } from '@/analytics/utils/sidebar-analytics'
import { SideNavContainer } from './SideNavContainer'
import { SideNavHeader } from './SideNavHeader'
import { SideNavSection, SideNavDivider, SideNavSpacer } from './SideNavSection'
import { SideNavFooter } from './SideNavFooter'
import { ChatHistory } from './ChatHistory'
import { MatterRecords } from './MatterRecords'
import { SideNavItem } from '../side-nav-item/SideNavItem'

// =============================================================================
// Shared Types
// =============================================================================

/**
 * Configuration for a single navigation item in the sidebar.
 *
 * Used to define clickable navigation links with icons, labels, and optional
 * indicators like lock icons (for anonymous users) or beta tags.
 */
export type SideNavItemConfig = {
  /** Unique identifier for the item, used for active state matching */
  id: string
  /** Display text for the navigation item */
  label: string
  /** Navigation target URL */
  href: string
  /** Icon component to display alongside the label */
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  /** Icon to use in the contracted rail when the design calls for a different glyph than the expanded nav */
  railIcon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** Shows a lock icon indicating the item requires authentication */
  showLock?: boolean
  /** Shows a beta tag next to the item */
  showBetaTag?: boolean
  /** Additional classes to apply to the icon */
  iconClassName?: string
  /** Optional click handler for item-specific actions (runs before navigation) */
  onClick?: () => void
  /** Visual weight of the item. 'subtle' renders a smaller, muted style for lightweight actions (e.g. "Back to all matters"). Defaults to 'default'. */
  variant?: 'default' | 'subtle'
}

/**
 * Configuration for the Matter Records sidebar section.
 *
 * Thin config: the `MatterRecords` component owns asset selection, item building,
 * permission checks, and the Build a new record modal internally.
 */
export type SideNavMatterRecordsConfig = {
  matterId: string
}

/**
 * Configuration for the matter nav's "Chat History" section.
 *
 * Undefined when the feature is flagged off, so `SideNavMatter` renders the nav exactly as it
 * does today.
 */
export type SideNavChatHistoryConfig = {
  /** The active matter — ChatHistory fetches and owns its own chat list and loading state */
  matterId: string
  /** The id of the currently open chat, for active-row highlighting */
  activeChatId?: string
  allowNewChat?: boolean
}

// =============================================================================
// SideNavDashboard - Dashboard navigation (Matters, Templates)
// =============================================================================

export type SideNavDashboardProps = {
  /** Navigation items to display */
  items: SideNavItemConfig[]
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Called after any nav item click completes (e.g., to close mobile sidebar) */
  onNavigate?: () => void
  /** Render prop for footer content (account menu, support, etc.) */
  renderFooter: () => React.ReactNode
  /** Logo URL */
  logoUrl: string
  /** Logo alt text */
  logoAlt?: string
  /** Whether to apply fixed height sizing to the logo (34px) */
  hasFixedLogoHeight?: boolean
  /** Optional callback when logo is clicked (e.g., to close mobile sidebar) */
  onLogoClick?: () => void
  /** Contracts the nav to the icon rail. Omit to render no toggle (mobile, flag off). */
  onContract?: () => void
  /** Ref to the contract toggle, so the owner can move focus here when the nav expands. */
  contractToggleRef?: React.Ref<HTMLButtonElement>
  /* Whether the nav is rendered in the slideover */
  isSlideover?: boolean
}

/**
 * Dashboard variant of the SideNav for root-level navigation.
 *
 * Displays a flat list of navigation items (e.g., Matters, Templates).
 */
export function SideNavDashboard({
  items,
  activeItemId,
  onNavigate,
  renderFooter,
  logoUrl,
  logoAlt = 'Logo',
  hasFixedLogoHeight = false,
  onLogoClick,
  onContract,
  contractToggleRef,
  isSlideover = false,
}: SideNavDashboardProps) {
  const { trackEvent } = useAnalytics()
  return (
    <SideNavContainer data-ds-component="SideNav" data-ds-variant="dashboard">
      <SideNavHeader
        logoUrl={logoUrl}
        logoAlt={logoAlt}
        hasFixedLogoHeight={hasFixedLogoHeight}
        onLogoClick={onLogoClick}
        onContract={onContract}
        contractToggleRef={contractToggleRef}
        isSlideover={isSlideover}
      />

      <nav className="flex flex-1 flex-col">
        <SideNavSpacer isSlideover={isSlideover} />

        <SideNavSection className="flex-1">
          <div className="flex flex-col gap-4 px-2">
            {items.map((item) => (
              <SideNavItem
                key={item.id}
                to={item.href}
                icon={item.icon}
                label={item.label}
                isActive={activeItemId === item.id}
                showLock={item.showLock}
                showBetaTag={item.showBetaTag}
                onClick={() => {
                  trackSidebarLinkClicked(trackEvent, item.id, false)
                  item.onClick?.()
                  onNavigate?.()
                }}
                iconClassName={item.iconClassName}
                variant={item.variant}
              />
            ))}
          </div>

          <SideNavFooter>{renderFooter()}</SideNavFooter>
        </SideNavSection>
      </nav>
    </SideNavContainer>
  )
}

// =============================================================================
// SideNavMatter - Matter context navigation (AI Assistant, Medical, etc.)
// =============================================================================

export type SideNavMatterProps = {
  /** Top section items (e.g., Home, Matter Overview, AI Assistant, Matter Files) */
  topItems: SideNavItemConfig[]
  /** Non-collapsible Matter Records section below the top items. */
  matterRecords?: SideNavMatterRecordsConfig
  /** Optional Chat History section, rendered below the records section and above the footer. Undefined = today's layout, unchanged. */
  chatHistory?: SideNavChatHistoryConfig
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Called after any nav item click completes (e.g., to close mobile sidebar) */
  onNavigate?: () => void
  /** Render prop for footer content (account menu, support, etc.) */
  renderFooter: () => React.ReactNode
  /** Logo URL */
  logoUrl: string
  /** Logo alt text */
  logoAlt?: string
  /** Whether to apply fixed height sizing to the logo (34px) - Prev isPaxtonLogo */
  hasFixedLogoHeight?: boolean
  /** Optional callback when logo is clicked (e.g., to close mobile sidebar) */
  onLogoClick?: () => void
  /** Contracts the nav to the icon rail. Omit to render no toggle (mobile, flag off). */
  onContract?: () => void
  /** Ref to the contract toggle, so the owner can move focus here when the nav expands. */
  contractToggleRef?: React.Ref<HTMLButtonElement>
  /* Whether the nav is rendered in the slideover */
  isSlideover?: boolean
}

/**
 * Matter variant of the SideNav for navigation within a matter context.
 *
 * Displays a structured layout with:
 * - Top items (Home, Matter Overview, AI Assistant, Matter Files)
 * - Divider
 * - Collapsible group (e.g., Medical Overview with Chronology and Billing)
 */
export function SideNavMatter({
  topItems,
  matterRecords,
  chatHistory,
  activeItemId,
  onNavigate,
  renderFooter,
  logoUrl,
  logoAlt = 'Logo',
  hasFixedLogoHeight = false,
  onLogoClick,
  onContract,
  contractToggleRef,
  isSlideover = false,
}: SideNavMatterProps) {
  const { trackEvent } = useAnalytics()
  const matterRecordsContent = matterRecords && <MatterRecords matterId={matterRecords.matterId} activeItemId={activeItemId} onNavigate={onNavigate} />

  return (
    <SideNavContainer data-ds-component="SideNav" data-ds-variant="matter">
      <SideNavHeader
        logoUrl={logoUrl}
        logoAlt={logoAlt}
        hasFixedLogoHeight={hasFixedLogoHeight}
        onLogoClick={onLogoClick}
        onContract={onContract}
        contractToggleRef={contractToggleRef}
        isSlideover={isSlideover}
      />

      <nav className="flex flex-1 min-h-0 flex-col">
        <SideNavSpacer isSlideover={isSlideover} />

        <SideNavSection>
          <div className="flex flex-col gap-1 px-2 pb-1">
            {/* Top items */}
            {topItems.map((item) => (
              <SideNavItem
                key={item.id}
                to={item.href}
                icon={item.icon}
                label={item.label}
                isActive={activeItemId === item.id}
                showLock={item.showLock}
                showBetaTag={item.showBetaTag}
                onClick={() => {
                  trackSidebarLinkClicked(trackEvent, item.id, false)
                  item.onClick?.()
                  onNavigate?.()
                }}
                iconClassName={item.iconClassName}
                variant={item.variant}
              />
            ))}
          </div>
        </SideNavSection>

        {matterRecords && !chatHistory && <SideNavDivider />}

        {chatHistory ? (
          <>
            {/* The top items list above ends with its own 4px bottom padding, so 12px here nets a
                16px gap above Matter Records. */}
            {matterRecordsContent && <SideNavSection className="pt-3">{matterRecordsContent}</SideNavSection>}

            {/* Chat History scrolls independently of the rest of the nav; the footer stays pinned below it. */}
            <SideNavSection className={clsx('flex-1 min-h-0', matterRecordsContent ? 'pt-4' : 'pt-3')}>
              <ChatHistory
                matterId={chatHistory.matterId}
                activeChatId={chatHistory.activeChatId}
                onNavigate={onNavigate}
                allowNewChat={chatHistory.allowNewChat}
              />
            </SideNavSection>

            <SideNavSection>
              <SideNavFooter>{renderFooter()}</SideNavFooter>
            </SideNavSection>
          </>
        ) : (
          <SideNavSection className="flex-1">
            {matterRecordsContent}
            <SideNavFooter>{renderFooter()}</SideNavFooter>
          </SideNavSection>
        )}
      </nav>
    </SideNavContainer>
  )
}
